import React, { useMemo } from 'react';
import { Spinner, useTheme, ScrollView } from 'tamagui';

import { CobuContainer, CobuStack, CobuText } from '@cobu/ui';
import { StatsCard } from './StatsCard';
import {useReferralStats}  from '../../hooks/useReferralStats';
import { Strings } from '../../constants/Strings';  

const referralStatsStrings = Strings.get().referral.stats;

const statsKeys = {
  referralLeadCount: 'referralLeadCount',
  pageVisitCount: 'pageVisitCount',
  leaseLeadsCount: 'leaseLeadsCount',
} as const;

const statsHeadings = {
  [statsKeys.referralLeadCount]: referralStatsStrings.labels.leads,
  [statsKeys.pageVisitCount]: referralStatsStrings.labels.views,
  [statsKeys.leaseLeadsCount]: referralStatsStrings.labels.leases,
};

const statsOrder = [
  statsKeys.pageVisitCount,
  statsKeys.referralLeadCount,
  statsKeys.leaseLeadsCount,
];

interface ReferralLeadListItem {
  name: string;
  email: string;
  date: string;
  status: string;
}

// Helper function to format date
const formatDate = (dateString?: string): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
};

export const ReferralStats: React.FC = () => {
  const theme = useTheme();
  const { referralStats, isLoading, error } = useReferralStats();

  const processedData = useMemo(() => {
    if (!referralStats) return null;

    const { referralLeads, ...statsData } = referralStats;

    // Process stats cards
    const statsCards = statsOrder
      .map(key => [key, statsData[key as keyof typeof statsData]])
      .map(([key, value]) => ({
        stat: value as number,
        label: statsHeadings[key as keyof typeof statsHeadings] || '',
        key: key as string,
      }));

    // Process referral leads
    const referralLeadData: ReferralLeadListItem[] = referralLeads.map(lead => ({
      name: `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
      email: lead.email || '',
      date: formatDate(lead.createdAt),
      status: lead.referral_status || '',
    }));

    return { statsCards, referralLeadData };
  }, [referralStats]);

  if (isLoading) {
    return (
      <CobuContainer f={1} bg="$white" jc="center" ai="center">
        <CobuStack ai="center" gap="$4">
          <Spinner size="large" color="$cobuTeal" />
          <CobuText variant="body" color="$cobuDarkBlue">
            {referralStatsStrings.loading}
          </CobuText>
        </CobuStack>
      </CobuContainer>
    );
  }

  if (error || !processedData) {
    return (
      <CobuContainer f={1} bg="$white" jc="center" ai="center" px="$4">
        <CobuText variant="body" color="$red" ta="center">
          {referralStatsStrings.errorMessage}
        </CobuText>
      </CobuContainer>
    );
  }

  const { statsCards, referralLeadData } = processedData;

  const renderStatsCard = ({ item, index }: { item: any; index: number }) => {
    const isLastElement = statsCards.length - 1 === index;

    return (
      <CobuStack key={item.key} f={1} layout="horizontal" mr={isLastElement ? 0 : '$2'}>
        <StatsCard statNumber={item.stat} label={item.label} />
      </CobuStack>
    );
  };

  const renderLead = ({ item }: { item: ReferralLeadListItem }) => {
    return (
      <CobuStack
        layout="horizontal"
        ai="center"
        jc="space-between"
        py="$2"
        bbw={1}
        bbc="$backgroundMuted"
      >
        <CobuStack f={3}>
          <CobuText fontSize="$3" fontWeight="medium" color="$black">
            {item.name}
          </CobuText>
          <CobuText fontSize="$2" color="$black">
            {item.email}
          </CobuText>
        </CobuStack>

        <CobuStack f={2}>
          <CobuText fontSize="$2" color="$black" ta="right">
            {item.date}
          </CobuText>
        </CobuStack>
      </CobuStack>
    );
  };

  const renderHeader = () => (
    <CobuStack>
      {/* Stats Cards */}
      <CobuStack layout="horizontal" gap="$2" mb="$6" pt="$4">
        {statsCards.map((item, index) => renderStatsCard({ item, index }))}
      </CobuStack>

      {/* Your Leads Section */}
      <CobuStack bbw={1} bbc="$backgroundMuted">
        <CobuText variant="heading" fontSize="$6" fontWeight="bold" color="$cobuDarkBlue" pb="$2">
          {referralStatsStrings.yourLeads}
        </CobuText>
      </CobuStack>

      <CobuStack layout="horizontal" ai="center" jc="space-between" pt="$2">
        <CobuText variant="body" fontSize="$2" fontWeight="bold" color="$black" f={3}>
          {referralStatsStrings.tableHeaders.who}
        </CobuText>

        <CobuText variant="body" fontSize="$2" fontWeight="bold" color="$black" f={2} ta="right">
          {referralStatsStrings.tableHeaders.dateReferred}
        </CobuText>
      </CobuStack>
    </CobuStack>
  );

  return (
    <CobuContainer f={1} bg="$white" px="$4">
      <ScrollView showsVerticalScrollIndicator={false}>
        {renderHeader()}
        {referralLeadData.map((item) => (
          <React.Fragment key={item.email}>{renderLead({ item })}</React.Fragment>
        ))}
      </ScrollView>
    </CobuContainer>
  );
};
